import React, { memo, useCallback, useEffect, useMemo } from 'react';
import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';

import {
  getEmailField,
  getExceptSpecialSymbolsTextField,
  getPhoneNumberField,
  getTextField,
} from '@lender/utils';
import { Button, Typography } from '@mui/material';
import {
  AutocompleteCDS,
  onAutocompleteChangeInForm,
  TextFieldCDS,
} from '@sharedCR/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { object } from 'yup';

import { LICENSE_NUMBER_MAX_LENGTH } from 'constants/loanApplication';
import { stateOptions, statesOptions } from 'constants/options';
import { PHONE_NUMBER_FORMAT } from 'constants/ui';
import useForm from 'hooks/useForm';
import { normalizeRequestPhoneNumber, normalizeViewPhoneNumber } from 'utils/data';
import { getZipField } from 'utils/validation';

import { closeDialogAction } from 'store/global/actions';
import { updateFeeProviderAction } from 'store/system/actions';
import { selectFeeProviderById } from 'store/system/selectors';

import { FeeProviderLocationTableTestIds } from './FeeProviderLocationTable.testIds';

const schema = object().shape({
  streetAddress: getTextField(),
  suite: getTextField({ isRequired: false }).nullable(),
  city: getTextField(),
  state: getTextField(),
  zipCode: getZipField({ isRequired: true }),
  contactPerson: getTextField({ isRequired: false }).nullable(),
  contactPhone: getPhoneNumberField({ isRequired: false }).nullable(),
  contactEmail: getEmailField({ isRequired: false }).nullable(),
  phone: getPhoneNumberField({ isRequired: false }).nullable(),
  email: getEmailField({ isRequired: false }).nullable(),
  licenseNumber: getTextField().nullable(),
  wireInformationBank: getExceptSpecialSymbolsTextField({ isRequired: false }).nullable(),
  wireInformationCity: getTextField({ isRequired: false }).nullable(),
  wireInformationState: getTextField({ isRequired: false }),
  wireInformationStateAbaNumber: getExceptSpecialSymbolsTextField({ isRequired: false }).nullable(),
  wireInformationAccountNumber: getExceptSpecialSymbolsTextField({ isRequired: false }).nullable(),
});

const FeeProviderLocationForm = ({
  onChangeLocationCallback,
  feeProviderId,
  feeProviderLocationId = '',
  isLoading,
  setIsLoading,
  formRef,
}) => {
  const dispatch = useDispatch();
  const isEditMode = !!feeProviderLocationId;

  const feeProvider = useSelector(selectFeeProviderById(feeProviderId));

  const initialLocationData = useMemo(() => {
    if (!isEditMode) {
      return {};
    }

    const location = feeProvider.locations.find(
      location => location.feeProviderLocationId === feeProviderLocationId,
    );

    return {
      ...location,
      contactPhone: normalizeViewPhoneNumber(location.contactPhone),
      phone: normalizeViewPhoneNumber(location.phone),
      state: stateOptions.find(({ value }) => value === location.state),
      wireInformationBank: location.wireInformation?.bank,
      wireInformationCity: location.wireInformation?.city,
      wireInformationState: stateOptions.find(
        ({ value }) => value === location.wireInformation?.state,
      ),
      wireInformationStateAbaNumber: location.wireInformation?.abaNumber,
      wireInformationAccountNumber: location.wireInformation?.accountNumber,
    };
  }, [feeProvider.locations, isEditMode, feeProviderLocationId]);

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    isFormChanged,
    validateForm,
    setInForm,
    validateField,
  } = useForm(initialLocationData);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      const {
        wireInformationBank,
        wireInformationCity,
        wireInformationState,
        wireInformationStateAbaNumber,
        wireInformationAccountNumber,
        ...otherFormValues
      } = form;

      const requestedLocation = {
        ...otherFormValues,
        contactEmail: form.contactEmail || null,
        email: form.email || null,
        state: form.state.value,
        contactPhone: normalizeRequestPhoneNumber(form.contactPhone),
        phone: normalizeRequestPhoneNumber(form.phone),
        wireInformation: {
          bank: wireInformationBank,
          city: wireInformationCity,
          state: wireInformationState?.value,
          abaNumber: wireInformationStateAbaNumber,
          accountNumber: wireInformationAccountNumber,
        },
      };

      const data = {
        feeProviderId,
        feeProviderData: feeProvider,
        ...(isEditMode
          ? {
              update: { [feeProviderLocationId]: requestedLocation },
            }
          : { create: [requestedLocation] }),
      };

      dispatch(updateFeeProviderAction(data))
        .then(() => {
          dispatch(closeDialogAction());

          if (onChangeLocationCallback) {
            onChangeLocationCallback({ id: feeProviderId });
          }
        })
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [
      dispatch,
      form,
      validateForm,
      setIsLoading,
      isEditMode,
      feeProvider,
      feeProviderLocationId,
      feeProviderId,
      onChangeLocationCallback,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <form
      noValidate
      className="p-24 w-800"
      data-ischanged={isFormChanged}
      data-testid={FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM}
      ref={formRef}
      onSubmit={onSubmit}
    >
      <div className="grid grid-cols-3 gap-24">
        <TextFieldCDS
          required
          dataTestId={
            FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_STREET_ADDRESS_INPUT
          }
          disabled={isLoading}
          error={!!validationErrors.streetAddress}
          helperText={validationErrors.streetAddress}
          id="streetAddress"
          label="Street address"
          maxLength={150}
          name="streetAddress"
          value={form.streetAddress}
          onChange={handleChange}
        />

        <TextFieldCDS
          dataTestId={FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_SUITE_INPUT}
          disabled={isLoading}
          error={!!validationErrors.suite}
          helperText={validationErrors.suite}
          id="suite"
          label="Suite"
          maxLength={150}
          name="suite"
          value={form.suite}
          onChange={handleChange}
        />

        <div />

        <TextFieldCDS
          required
          dataTestId={FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_CITY_INPUT}
          disabled={isLoading}
          error={!!validationErrors.city}
          helperText={validationErrors.city}
          id="city"
          label="City"
          maxLength={100}
          name="city"
          value={form.city}
          onChange={handleChange}
        />

        <AutocompleteCDS
          disablePortal
          required
          dataTestId={FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_STATE_SELECT}
          disabled={isLoading}
          error={!!validationErrors.state}
          helperText={validationErrors.state}
          label="State"
          name="state"
          options={statesOptions}
          value={form.state}
          onChange={onAutocompleteChange}
        />

        <TextFieldCDS
          required
          dataTestId={FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_ZIP_CODE_INPUT}
          disabled={isLoading}
          error={!!validationErrors.zipCode}
          helperText={validationErrors.zipCode}
          id="zipCode"
          label="Zip Code"
          maxLength={5}
          name="zipCode"
          value={form.zipCode}
          onChange={handleChange}
        />

        <TextFieldCDS
          dataTestId={
            FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_CONTACT_PERSON_INPUT
          }
          disabled={isLoading}
          error={!!validationErrors.contactPerson}
          helperText={validationErrors.contactPerson}
          id="contactPerson"
          label="Contact Person"
          maxLength={100}
          name="contactPerson"
          value={form.contactPerson}
          onChange={handleChange}
        />

        <NumberFormat
          customInput={TextFieldCDS}
          dataTestId={
            FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_CONTACT_PHONE_INPUT
          }
          disabled={isLoading}
          error={!!validationErrors.contactPhone}
          format={PHONE_NUMBER_FORMAT}
          helperText={validationErrors.contactPhone}
          label="Contact Phone"
          mask="_"
          name="contactPhone"
          type="tel"
          value={form.contactPhone}
          onChange={handleChange}
        />

        <TextFieldCDS
          dataTestId={
            FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_CONTACT_EMAIL_INPUT
          }
          disabled={isLoading}
          error={!!validationErrors.contactEmail}
          helperText={validationErrors.contactEmail}
          id="contact-email"
          label="Contact Email"
          maxLength={100}
          name="contactEmail"
          value={form.contactEmail}
          onChange={handleChange}
        />

        <NumberFormat
          customInput={TextFieldCDS}
          dataTestId={FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_PHONE_INPUT}
          disabled={isLoading}
          error={!!validationErrors.phone}
          format={PHONE_NUMBER_FORMAT}
          helperText={validationErrors.phone}
          label="Phone"
          mask="_"
          name="phone"
          type="tel"
          value={form.phone}
          onChange={handleChange}
        />

        <TextFieldCDS
          dataTestId={FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_EMAIL_INPUT}
          disabled={isLoading}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          id="email"
          label="Email"
          maxLength={100}
          name="email"
          value={form.email}
          onChange={handleChange}
        />

        <TextFieldCDS
          required
          dataTestId={
            FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_LICENSE_NUMBER_INPUT
          }
          disabled={isLoading}
          error={!!validationErrors.licenseNumber}
          helperText={validationErrors.licenseNumber}
          id="licenseNumber"
          label="License Number"
          maxLength={LICENSE_NUMBER_MAX_LENGTH}
          name="licenseNumber"
          value={form.licenseNumber}
          onChange={handleChange}
        />
      </div>

      <Typography className="mb-20 mt-24" variant="regular14">
        Wire Information
      </Typography>

      <div className="grid grid-cols-3 gap-24">
        <TextFieldCDS
          dataTestId={
            FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_WIRE_INFORMATION_BANK_INPUT
          }
          disabled={isLoading}
          error={!!validationErrors.wireInformationBank}
          helperText={validationErrors.wireInformationBank}
          id="wireInformationBank"
          label="Bank"
          name="wireInformationBank"
          value={form.wireInformationBank}
          onChange={handleChange}
        />

        <TextFieldCDS
          dataTestId={
            FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_WIRE_INFORMATION_CITY_INPUT
          }
          disabled={isLoading}
          error={!!validationErrors.wireInformationCity}
          helperText={validationErrors.wireInformationCity}
          id="wireInformationCity"
          label="City"
          maxLength={100}
          name="wireInformationCity"
          value={form.wireInformationCity}
          onChange={handleChange}
        />

        <AutocompleteCDS
          disablePortal
          dataTestId={
            FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_WIRE_INFORMATION_STATE_SELECT
          }
          disabled={isLoading}
          error={!!validationErrors.wireInformationState}
          helperText={validationErrors.wireInformationState}
          label="State"
          name="wireInformationState"
          options={statesOptions}
          value={form.wireInformationState}
          onChange={onAutocompleteChange}
        />

        <NumberFormat
          allowLeadingZeros
          allowNegative={false}
          customInput={TextFieldCDS}
          dataTestId={
            FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_WIRE_INFORMATION_ABA_NUMBER_INPUT
          }
          decimalSeparator={false}
          disabled={isLoading}
          error={!!validationErrors.wireInformationStateAbaNumber}
          helperText={validationErrors.wireInformationStateAbaNumber}
          id="wireInformationStateAbaNumber"
          label="ABA Number"
          maxLength={9}
          name="wireInformationStateAbaNumber"
          value={form.wireInformationStateAbaNumber || ''}
          onChange={handleChange}
        />

        <NumberFormat
          allowLeadingZeros
          allowNegative={false}
          customInput={TextFieldCDS}
          dataTestId={
            FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_WIRE_INFORMATION_ACCOUNT_NUMBER_INPUT
          }
          decimalSeparator={false}
          disabled={isLoading}
          error={!!validationErrors.wireInformationAccountNumber}
          helperText={validationErrors.wireInformationAccountNumber}
          id="wireInformationAccountNumber"
          label="Account Number"
          maxLength={9}
          name="wireInformationAccountNumber"
          value={form.wireInformationAccountNumber || ''}
          onChange={handleChange}
        />
      </div>

      <Button
        className="self-start mt-24"
        data-testid={FeeProviderLocationTableTestIds.FEE_PROVIDER_LOCATION_FORM_SUBMIT_BUTTON}
        disabled={isLoading || !isFormChanged}
        type="submit"
        variant="contained"
      >
        {isEditMode ? 'Edit' : 'Add'}
      </Button>
    </form>
  );
};

FeeProviderLocationForm.propTypes = {
  feeProviderId: PropTypes.string.isRequired,
  formRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  feeProviderLocationId: PropTypes.string,
  onChangeLocationCallback: PropTypes.func,
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(FeeProviderLocationForm);
